import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { BackButton } from '../../components/BackButton.js';
import {
  Alert,
  Badge,
  backLinkClasses,
  Icon,
  Page,
  PageHeader,
  isNotFound,
} from '@evogard/ui';
import {
  AgendaClientFile,
  useAgendaClient,
  type ClientView,
  type MeasuredClient,
} from './AgendaClient.js';
import { formatDocument } from '@evogard/domain';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * Um CLIENTE da agenda, na página própria — a ficha que o consultor abre com
 * o cliente na linha (`AgendaClientFile`), com o carro aberto e a aba no
 * endereço.
 *
 * A agenda abre o cliente na própria linha desde 21/10/2026; esta página fica
 * para quem chega pela placa ou por link — o painel do Extrato leva até ela —,
 * e por isso o "voltar" do navegador precisa devolver a pessoa ao carro que
 * ela abriu. Trocar de aba não: ela substitui o endereço em vez de empilhar.
 */
export function AgendaMemberScreen() {
  const { code } = useParams({ from: '/authenticated/sales/portfolio/members/$code' });
  const search = useSearch({ from: '/authenticated/sales/portfolio/members/$code' });
  const navigate = useNavigate();
  const query = useAgendaClient(code, search.consultant);

  const data = query.data;
  const measured = data?.estado === 'medido' ? (data as MeasuredClient) : undefined;
  const vehicles = measured?.vehicles.length ?? 0;
  const vehicle = measured?.vehicles.find((v) => v.code === search.vehicle);
  const opened = vehicle !== undefined;
  /*
   * Sem `consultant` no endereço, a agenda é a de quem está logado — e "com
   * este consultor" seria a tela falando de você na terceira pessoa.
   */
  const own = search.consultant === undefined;

  const go = (vehicle: string | undefined, view: ClientView, replace = false) =>
    void navigate({
      to: '/sales/portfolio/members/$code',
      params: { code },
      search: { consultant: search.consultant, vehicle, view },
      replace,
    });

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{
          to: '/sales/portfolio',
          search: { consultant: search.consultant, page: 1 },
          label: 'Ver a carteira',
        }}
      >
        Não há veículo dele com {own ? 'você' : 'este consultor'}. Confira o código, ou
        procure o associado na carteira.
      </MissingRecord>
    );
  }

  return (
    <Page measure="cheia">
      <PageHeader
        back={
          /*
           * Volta para onde se veio — o relatório da premiação, quando a placa
           * foi clicada lá (28/10/2026). Sem página anterior, a agenda.
           */
          <BackButton
            fallback={
              <Link
                to="/sales/portfolio"
                search={{ consultant: search.consultant, page: 1 }}
                className={backLinkClasses()}
              >
                <Icon nome="seta-esquerda" size={16} />
                Voltar à carteira
              </Link>
            }
          />
        }
        /* Com o carro aberto, o cabeçalho da referência do dono (28/10/2026). */
        eyebrow={
          opened ? <span className="rotulo text-texto-suave">Associado</span> : undefined
        }
        title={
          vehicle ? (
            <>
              {measured?.member.name ?? 'Associado da carteira'}
              <span className="font-mono text-base text-texto-suave">
                {' — '}
                {vehicle.plate ?? 'sem placa'}
              </span>
            </>
          ) : (
            (measured?.member.name ?? 'Associado da carteira')
          )
        }
        aside={
          measured &&
          (opened
            ? measured.claims.some((c) => c.vehicleCode === vehicle?.code)
            : measured.claims.length > 0) && <Badge tone="alerta">Em sinistro</Badge>
        }
      >
        {opened ? (
          measured?.member.document ? (
            <span className="tabular-nums">
              {formatDocument(measured.member.document)}
            </span>
          ) : undefined
        ) : measured ? (
          <>
            Associado <span className="tabular-nums">{measured.member.code}</span> ·{' '}
            {vehicles} {vehicles === 1 ? 'veículo' : 'veículos'}
            {own ? '.' : ' com este consultor.'}
          </>
        ) : own ? (
          'Os veículos, os boletos e os atendimentos deles.'
        ) : (
          'Os veículos com este consultor, os boletos e os atendimentos deles.'
        )}
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {data?.estado === 'sem_vinculo' && <Alert tone="alerta">{data.message}</Alert>}

      {(query.isLoading || measured) && (
        <AgendaClientFile
          measured={measured}
          loading={query.isLoading}
          selected={search.vehicle}
          view={search.view ?? 'boletos'}
          onOpen={(vehicle) => go(vehicle, 'boletos')}
          onView={(view) => go(search.vehicle, view, true)}
        />
      )}
    </Page>
  );
}
